/** Shared SIP employee role permission helpers. */

import Cookies from 'js-cookie';

function normalizePermissionsArray(value) {
  if (value == null || value === '') return [];
  let current = value;
  for (let i = 0; i < 3; i += 1) {
    if (Array.isArray(current)) {
      return current.filter((p) => typeof p === 'string');
    }
    if (typeof current === 'string') {
      try {
        current = JSON.parse(current);
      } catch {
        return [];
      }
      continue;
    }
    break;
  }
  return [];
}

export function getEmployeePermissions(employeeData) {
  return normalizePermissionsArray(employeeData?.permission?.permissions_json);
}

/** SIP extension id for the logged-in employee (profile or buddy cookie). */
export function getEmployeeBuddy(employeeData) {
  return String(
    employeeData?.buddy ||
    Cookies.get('buddy') ||
    ''
  ).trim();
}

function getBuddyIdentifiers(employeeData) {
  const ids = new Set();
  const buddy = getEmployeeBuddy(employeeData);
  if (buddy) ids.add(buddy);
  const redirectIn = String(employeeData?.redirect_input || '').trim();
  const redirectOut = String(employeeData?.redirect_output || '').trim();
  if (redirectIn) ids.add(redirectIn);
  if (redirectOut) ids.add(redirectOut);
  return ids;
}

/** Substring match (Solr-style *value*), both directions. */
function likeContains(haystack, needle) {
  if (!haystack || !needle) return false;
  return haystack.includes(needle) || needle.includes(haystack);
}

export function isOwnRecordingRow(employeeData, row) {
  const ids = getBuddyIdentifiers(employeeData);
  if (ids.size === 0) return true;

  const candidates = [
    row?.extension,
    row?.destination,
    row?.in_num,
    row?.ext_num,
    row?.transfer_to,
  ]
    .filter((v) => v != null && v !== '' && v !== 'null')
    .map((v) => String(v).trim());

  const parts = candidates.flatMap((v) =>
    v.split(/[-/]/).map((p) => String(p).trim()).filter(Boolean)
  );

  const valuesToCheck = [...candidates, ...parts];

  for (const id of ids) {
    if (valuesToCheck.some((v) => likeContains(v, id))) return true;
  }
  return false;
}

/** Whether the current user may play a call recording row. */
export function canListenToRecording(employeeData, row) {
  if (!row?.uniqueid) return false;
  if (row?.isAudio === false) return false;

  const buddy = getEmployeeBuddy(employeeData);
  if (!buddy) return true;

  const perms = getEmployeePermissions(employeeData);
  const hasListenAll = perms.includes('recordings_listen_all');
  const hasListenOwn = perms.includes('recordings_listen_own');

  if (!employeeData?.permission) {
    return isOwnRecordingRow(employeeData, row);
  }

  if (!hasListenAll && !hasListenOwn) return false;
  if (hasListenAll) return true;
  return isOwnRecordingRow(employeeData, row);
}

export function isUrsSession() {
  return !!localStorage.getItem('access_token_urs');
}

export function isSipEmployee(employeeData) {
  return !!getEmployeeBuddy(employeeData);
}

export function hasHistoryView(employeeData) {
  if (isUrsSession()) return true;
  if (!isSipEmployee(employeeData)) return true;
  const perms = getEmployeePermissions(employeeData);
  return perms.includes('history_view')
    || perms.includes('call_history_own')
    || perms.includes('call_history_all')
    || perms.includes('group_history_view');
}

export function hasOnlineView(employeeData) {
  if (isUrsSession()) return true;
  if (!isSipEmployee(employeeData)) return true;
  if (!employeeData?.permission) return true;
  return getEmployeePermissions(employeeData).includes('online_view');
}

export function hasCrmIntegrationsManage(employeeData) {
  if (isUrsSession()) return true;
  if (!isSipEmployee(employeeData)) return true;
  if (!employeeData?.permission) return true;
  return getEmployeePermissions(employeeData).includes('crm_integrations_manage');
}

export function hasEmployeeTelegram(employeeData) {
  return (employeeData?.telegrams?.length ?? 0) > 0;
}

export function hasEmployeeWhatsApp(employeeData) {
  const plural = employeeData?.whatsapps;
  const singular = employeeData?.whatsapp;
  if (Array.isArray(plural) && plural.length > 0) return true;
  if (Array.isArray(singular) && singular.length > 0) return true;
  return false;
}

/** Telegram connected for this user (SIP profile telegrams or URS/localStorage). */
export function hasTelegramConnected(employeeData, options = {}) {
  const { ursTelegramAccountsCount = 0 } = options;
  if (hasEmployeeTelegram(employeeData)) return true;
  if (isSipEmployee(employeeData)) return false;
  if (ursTelegramAccountsCount > 0) return true;
  if (typeof localStorage !== 'undefined' && localStorage.getItem('telegram')) return true;
  return false;
}

/** WhatsApp connected for this user (SIP profile whatsapps or URS/API/localStorage). */
export function hasWhatsAppConnected(employeeData, options = {}) {
  const { ursWhatsAppAccountsCount = 0 } = options;
  if (hasEmployeeWhatsApp(employeeData)) return true;
  if (isSipEmployee(employeeData)) return false;
  if (ursWhatsAppAccountsCount > 0) return true;
  if (typeof localStorage !== 'undefined' && localStorage.getItem('whatsapp')) return true;
  return false;
}

export function hasMessagingHistory(employeeData) {
  return hasEmployeeTelegram(employeeData) || hasEmployeeWhatsApp(employeeData);
}

export function isMessagingHistoryPath(pathname = '') {
  const p = pathname || '';
  return p.startsWith('/history_telegram') || p.startsWith('/history_whatsapp');
}

/** First Telegram or WhatsApp history route for this employee. */
export function getMessagingHistoryRoute(employeeData) {
  const tg = employeeData?.telegrams?.[0];
  if (tg?.id != null) return `/history_telegram/${tg.id}`;
  if (hasEmployeeWhatsApp(employeeData)) return '/history_whatsapp';
  return null;
}

const ROUTE_BY_PERMISSION = [
  ['employees_read', '/employees'],
  ['groups_read', '/groups'],
  ['ivr_read', '/ivrs'],
  ['did_read', '/main_number'],
  ['crm_integrations_manage', '/auto_call_list'],
];

/** First menu route the SIP employee is allowed to open. */
export function getFirstAllowedRoute(employeeData) {
  if (!isSipEmployee(employeeData)) return '/';
  if (!hasHistoryView(employeeData)) {
    const messaging = getMessagingHistoryRoute(employeeData);
    if (messaging) return messaging;
  }
  const perms = getEmployeePermissions(employeeData);
  for (const [perm, route] of ROUTE_BY_PERMISSION) {
    if (perms.includes(perm)) return route;
  }
  const messaging = getMessagingHistoryRoute(employeeData);
  if (messaging) return messaging;
  if (hasReportView(employeeData)) return '/operator_report';
  return '/profile';
}

export const HISTORY_PATH_PREFIXES = [
  '/history',
  '/graphic',
  '/operator_history',
];

export const REPORT_PATH_PREFIXES = ['/operator_report'];

export function isHistoryPath(pathname = '') {
  const p = pathname || '';
  return HISTORY_PATH_PREFIXES.some(
    (prefix) => p === prefix || p.startsWith(`${prefix}/`)
  );
}

export function isReportPath(pathname = '') {
  const p = pathname || '';
  return REPORT_PATH_PREFIXES.some(
    (prefix) => p === prefix || p.startsWith(`${prefix}/`)
  );
}

export function hasReportView(employeeData) {
  if (isUrsSession()) return true;
  if (!isSipEmployee(employeeData)) return true;
  const perms = getEmployeePermissions(employeeData);
  return perms.includes('report_view_own') || perms.includes('report_view_all');
}

export function hasReportViewAll(employeeData) {
  if (isUrsSession()) return true;
  if (!isSipEmployee(employeeData)) return true;
  return getEmployeePermissions(employeeData).includes('report_view_all');
}

/** SIP employee may only view their own report (not view_all). */
export function hasReportViewOwnOnly(employeeData) {
  if (!getEmployeeBuddy(employeeData)) return false;
  const perms = getEmployeePermissions(employeeData);
  return perms.includes('report_view_own') && !perms.includes('report_view_all');
}

/** Whether SIP employee may only play recordings on their own legs (not listen_all). */
export function hasRecordingsListenOwnOnly(employeeData) {
  if (!getEmployeeBuddy(employeeData)) return false;
  const perms = getEmployeePermissions(employeeData);
  return perms.includes('recordings_listen_own') && !perms.includes('recordings_listen_all');
}

